'use client';

import { useEffect } from 'react';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type ConsentAwareGTMProps = {
  gtmId: string;
};

/** Delay GTM until after load + idle so it cannot compete with LCP. */
const GTM_LOAD_IDLE_TIMEOUT_MS = 3500;

const scheduleAfterLoad = (callback: () => void) => {
  const run = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: GTM_LOAD_IDLE_TIMEOUT_MS });
    } else {
      setTimeout(callback, GTM_LOAD_IDLE_TIMEOUT_MS);
    }
  };

  if (document.readyState === 'complete') {
    run();
    return;
  }

  window.addEventListener('load', run, { once: true });
};

/**
 * Loads GTM after the page load event (post-LCP) and wires Cookiebot consent updates.
 * Consent defaults are set inline in layout <head> before any third-party script runs.
 */
export const ConsentAwareGTM = ({ gtmId }: ConsentAwareGTMProps) => {
  useEffect(() => {
    const updateConsent = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cb = (window as any).Cookiebot;
      if (!cb?.consent) return;

      window.gtag?.('consent', 'update', {
        ad_storage: cb.consent.marketing ? 'granted' : 'denied',
        ad_user_data: cb.consent.marketing ? 'granted' : 'denied',
        ad_personalization: cb.consent.marketing ? 'granted' : 'denied',
        analytics_storage: cb.consent.statistics ? 'granted' : 'denied',
        functionality_storage: cb.consent.preferences ? 'granted' : 'denied',
        personalization_storage: cb.consent.preferences ? 'granted' : 'denied',
        security_storage: 'granted',
      });
    };

    window.addEventListener('CookiebotOnAccept', updateConsent);
    window.addEventListener('CookiebotOnDecline', updateConsent);

    scheduleAfterLoad(() => {
      if (document.getElementById('gtm-script')) return;

      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

      const script = document.createElement('script');
      script.id = 'gtm-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      document.head.appendChild(script);
    });

    return () => {
      window.removeEventListener('CookiebotOnAccept', updateConsent);
      window.removeEventListener('CookiebotOnDecline', updateConsent);
    };
  }, [gtmId]);

  return null;
};
